import LegalShell, { H2, H3, P, UL } from './LegalShell';
import { COMPANY, formatAddress } from './companyInfo';

// Pro Haul Terms of Service.
//
// THIS IS INITIAL TEMPLATE COPY suitable for Stripe activation
// verification and early customer acceptance. Have it reviewed by
// counsel before relying on for contested disputes. Edit
// companyInfo.js (not this file) to swap entity name, address,
// support email, governing state, and effective date.

export default function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <P>
        These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) govern your access to and use of
        the {COMPANY.productName} platform, including any web applications, mobile
        applications, APIs, and related services (collectively, the &ldquo;<strong>Service</strong>&rdquo;)
        offered by {COMPANY.legalName} (&ldquo;<strong>{COMPANY.productName}</strong>,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an account, accessing,
        or using the Service, you agree to be bound by these Terms. If you do not agree, do
        not use the Service.
      </P>

      <H2>1. The Service</H2>
      <P>
        {COMPANY.productName} provides a multi-tenant transportation management system (TMS) for
        motor carriers and owner-operators. The Service includes load board access,
        active load tracking, driver profile and DOT compliance management, payroll and
        settlement processing, accounting, IFTA reporting, maintenance records, document
        storage, and related operational tooling. Specific features available to your account
        depend on the subscription plan you select.
      </P>

      <H2>2. Eligibility &amp; Accounts</H2>
      <P>
        You must be at least 18 years old and authorised to bind the entity on whose behalf
        you are using the Service. You agree to provide accurate, current, and complete
        registration information and to keep it current. You are responsible for safeguarding
        your account credentials and for all activity that occurs under your account.
      </P>
      <P>
        Carrier accounts are tenant-isolated: data you submit to your carrier workspace is
        not visible to other carriers. Staff members you invite to your workspace operate
        within the role you assign (Admin, HR, Accounting, Dispatch) and are subject to
        these Terms as users of your account.
      </P>

      <H2>3. Subscription Plans &amp; Billing</H2>
      <P>
        The Service is offered on subscription plans:
      </P>
      <UL>
        <li>
          <strong>Pro Haul Owner-Operator</strong> &mdash; flat monthly fee, single user, intended
          for solo owner-operators driving their own truck.
        </li>
        <li>
          <strong>Pro Haul Fleet</strong> &mdash; tiered monthly fee with a base price covering up
          to two drivers, plus a per-driver fee for each additional driver. Intended for
          carriers with employee drivers.
        </li>
      </UL>
      <P>
        Current pricing is displayed inside the Service at the Billing tab and on the public
        marketing pages, and is also reflected on the Stripe Checkout page presented when
        you subscribe. All subscriptions are billed in advance for the upcoming month
        unless otherwise stated.
      </P>

      <H3>Payment Processing</H3>
      <P>
        Payments are processed by Stripe, Inc. (&ldquo;Stripe&rdquo;). By subscribing, you
        authorise us, via Stripe, to charge your payment method on a recurring basis.
        Card details are stored by Stripe, not by us. Your use of Stripe is subject to
        Stripe&rsquo;s terms and privacy policy.
      </P>

      <H3>Free Trial</H3>
      <P>
        New carriers may receive a free trial (currently 14 days). Trial duration may
        change for new signups; existing trials are not retroactively shortened. At the
        end of the trial, full access requires an active subscription.
      </P>

      <H3>Plan Changes</H3>
      <P>
        You may upgrade or downgrade between the Owner-Operator and Fleet plans at any
        time from the Billing tab. Plan changes are pro-rated by Stripe: unused time on
        your current plan is credited against the first invoice of the new plan, and any
        additional amount due is charged accordingly. Seat changes on the Fleet plan
        adjust your next invoice on a pro-rata basis.
      </P>

      <H3>Cancellation &amp; Refunds</H3>
      <P>
        You may cancel your subscription at any time from the Stripe-powered billing
        portal accessible from the Billing tab. On cancellation, your subscription
        remains active through the end of the current paid period; access ends at that
        point. Except where required by law, subscription fees are non-refundable for
        partial periods. Promotional discounts and coupons, where applied, follow the
        terms of the specific promotion.
      </P>

      <H2>4. Acceptable Use</H2>
      <P>You agree not to:</P>
      <UL>
        <li>
          Use the Service for any purpose that violates applicable law, including but not
          limited to federal motor carrier safety regulations, DOT/FMCSA rules, employment
          and tax law, anti-discrimination laws, or sanctions regulations;
        </li>
        <li>
          Upload or submit data you do not have the right to share, including driver,
          medical, or background information without the requisite consent or authority;
        </li>
        <li>
          Interfere with, disrupt, or attempt to gain unauthorised access to the Service,
          other accounts, computer systems, or networks connected to the Service;
        </li>
        <li>
          Reverse engineer, decompile, or extract the source code of the Service, except
          to the extent such restrictions are prohibited by applicable law;
        </li>
        <li>
          Resell, sublicense, or commercially exploit the Service or any portion of it
          without our express written permission;
        </li>
        <li>
          Use automated means (scrapers, bots, etc.) to access the Service in a manner
          that exceeds reasonable human usage or burdens our infrastructure.
        </li>
      </UL>

      <H2>5. Customer Data &amp; Derived Data</H2>
      <P>
        You retain all right, title, and interest in the data you submit to the Service
        (&ldquo;<strong>Customer Data</strong>&rdquo;), including driver records, load records,
        documents, accounting entries, and related operational data. You grant us a
        limited licence to host, process, transmit, and display Customer Data solely as
        necessary to provide the Service to you.
      </P>
      <P>
        You are responsible for the legality of Customer Data, for obtaining any consents
        required to upload it, and for your retention obligations under applicable law
        (including 49 CFR 391.51 driver qualification file retention).
      </P>
      <P>
        On termination of your account, we will retain Customer Data for a reasonable
        period to permit export, after which we will delete it except where retention is
        required by law or for legitimate audit and tax purposes.
      </P>
      <H3>Derived Data</H3>
      <P>
        You grant us the right to anonymise and aggregate Customer Data to create
        statistical, performance, behavioural, or other derived information that does
        not identify you, your carrier, your customers, your drivers, or any individual
        (&ldquo;<strong>Derived Data</strong>&rdquo;). We may use Derived Data for any
        business purpose, including product improvement, benchmarking, analytics,
        research, and training artificial-intelligence and machine-learning models.
        We own all right, title, and interest in Derived Data. Our use of Derived Data
        does not reduce our obligation to protect identifiable Customer Data in
        accordance with these Terms and our Privacy Policy.
      </P>

      <H2>6. Feedback and Submissions</H2>
      <P>
        If you submit any ideas, suggestions, feature requests, comments, reviews, or
        other feedback regarding the Service (collectively, &ldquo;<strong>Feedback</strong>&rdquo;),
        you grant us a perpetual, irrevocable, worldwide, royalty-free, sublicensable,
        and transferable licence to use, reproduce, modify, distribute, and
        commercially exploit such Feedback for any purpose, without obligation of
        attribution or compensation to you. To the extent permitted by law, you waive
        any moral rights in the Feedback. Nothing in this section reduces our
        confidentiality obligations with respect to identifiable Customer Data.
      </P>

      <H2>7. Electronic Communications &amp; SMS</H2>
      <P>
        By creating an account and providing your email address and/or mobile phone
        number, you consent to receive electronic communications from us related to
        the Service, including transactional, operational, security, billing, and
        product-update messages. You also consent to receive promotional and marketing
        communications from us, which you may opt out of as described below.
      </P>
      <H3>SMS / Text Messaging</H3>
      <P>
        We may send recurring text messages to the mobile phone number you provide,
        including load assignment alerts, driver-document expiry warnings, billing
        notifications, security codes, and (with your consent) promotional updates.
        Message frequency varies based on your account activity.
        <strong> Message and data rates may apply.</strong> Consent to receive
        promotional text messages is not a condition of subscribing to the Service.
      </P>
      <P>
        You may opt out of promotional SMS at any time by replying <strong>STOP</strong>
        {' '}to any message we send; reply <strong>HELP</strong> for help, or contact{' '}
        <a href={`mailto:${COMPANY.supportEmail}`} className="text-prohaul-red hover:underline">
          {COMPANY.supportEmail}
        </a>
        . Operational messages necessary for the Service (account security alerts,
        billing failures, legally required notices) may continue after a promotional
        opt-out so long as your account remains active. You may also manage
        notification preferences from within your account settings.
      </P>
      <H3>Email</H3>
      <P>
        Transactional and account-related emails (invoices, security alerts, invitation
        confirmations, plan changes) are sent regardless of marketing preferences and
        cannot be opted out of while the account remains active. Promotional emails
        include an unsubscribe link in the footer and are governed by the CAN-SPAM Act.
      </P>

      <H2>8. Third-Party Services</H2>
      <P>
        The Service relies on third-party providers for hosting, payments, email delivery,
        artificial intelligence features, and mapping/routing. Your use of features that
        depend on these providers is also subject to the providers&rsquo; own terms. We
        list these providers in our Privacy Policy.
      </P>
      <P>
        The Service may also display data from external sources (for example, market load
        listings or FMCSA carrier information). We do not warrant the accuracy of
        third-party data and disclaim liability for decisions made in reliance on it.
      </P>

      <H2>9. Compliance Services (Where Offered)</H2>
      <P>
        Where {COMPANY.productName} facilitates ordering of regulated compliance services
        (including but not limited to drug and alcohol testing, random testing program
        enrolment, motor vehicle record pulls, Pre-employment Screening Program reports,
        FMCSA Drug &amp; Alcohol Clearinghouse queries, and background checks), such
        services may be provided by independent third-party providers (including
        consortium / third-party administrators, laboratories, and data furnishers).
        You authorise us and our providers to perform the services you order on your
        behalf and to share necessary data (including driver-identifying information you
        submit) for that purpose. Fees for ordered services are billed through your
        existing subscription invoice unless otherwise stated.
      </P>

      <H2>10. Intellectual Property</H2>
      <P>
        The Service, including all software, designs, text, graphics, logos, and the
        {' '}{COMPANY.productName} name and marks, is owned by {COMPANY.legalName} and is
        protected by intellectual property laws. Nothing in these Terms transfers any
        ownership of the Service to you; you receive only the limited right to access
        and use the Service in accordance with these Terms.
      </P>

      <H2>11. Export Controls &amp; Sanctions</H2>
      <P>
        The Service is controlled and operated from the United States and is subject to
        U.S. export-control and economic-sanctions laws, including the U.S. Export
        Administration Regulations (EAR) and regulations administered by the U.S.
        Department of the Treasury&rsquo;s Office of Foreign Assets Control (OFAC).
      </P>
      <P>
        You represent and warrant that: (a) you are not located in, ordinarily resident
        in, or organised under the laws of any country or region subject to comprehensive
        U.S. sanctions (currently including, without limitation, Cuba, Iran, North
        Korea, Syria, and the Crimea, Donetsk, and Luhansk regions of Ukraine); (b) you
        are not identified on any U.S. government denied- or restricted-party list,
        including OFAC&rsquo;s Specially Designated Nationals and Blocked Persons (SDN)
        List; (c) you will not access, use, export, re-export, transfer, or make
        available the Service in violation of any applicable export-control or
        sanctions law; and (d) you will not allow any prohibited party to access or
        benefit from the Service.
      </P>
      <P>
        We may suspend or terminate your access to the Service, with or without notice,
        if we determine in good faith that continued provision would violate these laws.
      </P>

      <H2>12. Disclaimer of Warranties</H2>
      <P>
        TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo;
        AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
        IMPLIED, OR STATUTORY, INCLUDING WITHOUT LIMITATION ANY WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR
        UNINTERRUPTED OR ERROR-FREE OPERATION. WE DO NOT WARRANT THAT THE SERVICE WILL
        MEET YOUR REQUIREMENTS, THAT DATA WILL BE PRESERVED WITHOUT LOSS, OR THAT THE
        SERVICE WILL OPERATE WITHOUT INTERRUPTION.
      </P>

      <H2>13. Limitation of Liability</H2>
      <P>
        TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL {COMPANY.legalName.toUpperCase()},
        ITS OFFICERS, EMPLOYEES, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOST DATA,
        OR BUSINESS INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH THE SERVICE,
        REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF ADVISED OF THE POSSIBILITY OF
        SUCH DAMAGES.
      </P>
      <P>
        OUR AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE
        SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO US FOR THE SERVICE DURING THE TWELVE
        (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
      </P>

      <H2>14. Indemnification</H2>
      <P>
        You agree to indemnify and hold harmless {COMPANY.legalName}, its officers,
        directors, employees, and agents from and against any claims, damages,
        liabilities, costs, and expenses (including reasonable attorneys&rsquo; fees)
        arising out of (a) your violation of these Terms; (b) your violation of any
        applicable law; (c) Customer Data you submit, including any third-party claim
        that such data infringes rights or was provided without authority; and (d) your
        use of the Service in connection with your business operations.
      </P>

      <H2>15. Termination</H2>
      <P>
        We may suspend or terminate your account at any time, with or without notice, if
        you breach these Terms, if your account is past due, or if continued service
        would expose us to legal or operational risk. You may terminate at any time by
        cancelling your subscription and ceasing use of the Service. Sections that by
        their nature should survive termination will survive.
      </P>

      <H2>16. Governing Law &amp; Disputes</H2>
      <P>
        These Terms are governed by the laws of the State of {COMPANY.governingState},
        excluding its conflict-of-laws rules. Subject to the next paragraph, the parties
        consent to the exclusive jurisdiction and venue of the state and federal courts
        located in {COMPANY.governingVenue} for any action arising out of or relating
        to these Terms.
      </P>
      <P>
        Either party may elect to resolve any dispute by binding arbitration
        administered by JAMS under its Streamlined Arbitration Rules, with arbitration
        seated in {COMPANY.governingVenue}. The parties waive any right to participate
        in a class action.
      </P>

      <H2>17. Changes to the Terms</H2>
      <P>
        We may update these Terms from time to time. If we make a material change, we
        will notify you by email or in-app notice and update the &ldquo;Effective&rdquo;
        date at the top of this page. Your continued use of the Service after the
        revised Terms take effect constitutes acceptance of the changes.
      </P>

      <H2>18. Contact</H2>
      <P>
        Questions about these Terms can be sent to{' '}
        <a href={`mailto:${COMPANY.supportEmail}`} className="text-prohaul-red hover:underline">
          {COMPANY.supportEmail}
        </a>{' '}
        or by mail to:
      </P>
      <P>
        {COMPANY.legalName}<br />
        {formatAddress()}
      </P>
    </LegalShell>
  );
}
